import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { ThumbsUp, ThumbsDown, TrendingUp, Filter, RefreshCw } from 'lucide-react';
import { isDemoAccount } from '../lib/auth';

interface FeedbackFormProps {
  onSubmit?: () => void;
}

export function FeedbackForm({ onSubmit }: FeedbackFormProps) {
  const [feedbackType, setFeedbackType] = useState<'feedback' | 'bug' | 'idea'>('feedback');
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState<number>(5);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState('');
  const [steps, setSteps] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim() || loading) return;

    setLoading(true);
    setError(null);

    try {
      // First, insert the feedback into the database
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to submit feedback');
      
      const isDemo = isDemoAccount(user?.email);
      
      const { error: submitError } = await supabase
        .from('feedback')
        .insert([
          {
            feedback,
            rating,
            type: feedbackType,
            error_details: feedbackType === 'bug' ? errorDetails : null,
            steps_to_reproduce: feedbackType === 'bug' ? steps : null,
            user_id: user?.id
          }
        ]);

      if (submitError) throw submitError;

      // Skip email notification for demo accounts
      if (!isDemo) {
        // Then, trigger the email notification
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            feedback,
            rating,
            type: feedbackType,
            error_details: errorDetails,
            steps_to_reproduce: steps,
            user_id: user?.id
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send notification');
        }
      }
      
      setSubmitted(true);
      setFeedback('');
      setRating(5);
      setErrorDetails('');
      setSteps('');
      onSubmit?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-4 bg-green-50 rounded-lg">
        <p className="text-green-700">Thank you for your feedback! 🙏</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-4 md:p-6 bg-white rounded-lg shadow">
      <h3 className="text-lg md:text-xl font-semibold">Help Us Improve</h3>
      
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <button
          type="button"
          onClick={() => setFeedbackType('feedback')}
          className={`p-3 md:p-4 rounded-lg border flex flex-col items-center gap-2 transition-colors ${
            feedbackType === 'feedback' 
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
              : 'border-gray-200 hover:border-indigo-200'
          }`}
        >
          <ThumbsUp className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-sm md:text-base">General Feedback</span>
        </button>

        <button
          type="button"
          onClick={() => setFeedbackType('bug')}
          className={`p-3 md:p-4 rounded-lg border flex flex-col items-center gap-2 transition-colors ${
            feedbackType === 'bug' 
              ? 'border-red-500 bg-red-50 text-red-700' 
              : 'border-gray-200 hover:border-red-200'
          }`}
        >
          <RefreshCw className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-sm md:text-base">Report Bug</span>
        </button>

        <button
          type="button"
          onClick={() => setFeedbackType('idea')}
          className={`p-3 md:p-4 rounded-lg border flex flex-col items-center gap-2 transition-colors ${
            feedbackType === 'idea' 
              ? 'border-yellow-500 bg-yellow-50 text-yellow-700' 
              : 'border-gray-200 hover:border-yellow-200'
          }`}
        >
          <TrendingUp className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-sm md:text-base">Suggest Idea</span>
        </button>
      </div>

      {feedbackType === 'feedback' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            How would you rate your experience?
          </label>
          <select
            value={rating}
            onChange={(e) => setRating(Number(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            {[1, 2, 3, 4, 5].map(num => (
              <option key={num} value={num}>{num} Star{num !== 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      )}

      {feedbackType === 'bug' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Error Details
            </label>
            <textarea
              value={errorDetails}
              onChange={(e) => setErrorDetails(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="What error message did you see? What went wrong?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Steps to Reproduce
            </label>
            <textarea
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="1. Click on...
2. Fill in...
3. Then..."
            />
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {feedbackType === 'feedback' ? "What could we improve?" :
           feedbackType === 'bug' ? "Additional Details" :
           "Describe Your Idea"}
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          placeholder={
            feedbackType === 'feedback' ? "Tell us what's working and what isn't..." :
            feedbackType === 'bug' ? "Any other context that might help us fix this..." :
            "Describe your feature idea in detail..."
          }
        />
      </div>

      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}

      <button
        type="submit"
        disabled={loading || !feedback.trim()}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        {loading ? 'Submitting...' : `Submit ${feedbackType === 'feedback' ? 'Feedback' : 
                feedbackType === 'bug' ? 'Bug Report' : 
                'Idea'}`}
      </button>
    </form>
  );
}